import { useResource } from './hooks';
import { ACTIVE_OPTIONS, type FilterOption } from '../components/FilterBar';

/**
 * Master data for the list-screen filter dropdowns.
 *
 * One request serves every screen: grades, subjects, designations, fee types, teachers and
 * staff, all from master tables. Deliberately not built by SELECT DISTINCT over the
 * transactional tables - master data is the authority on what a valid choice is, and it does
 * not shrink when the last row using a value is removed.
 */
export type Lookups = {
  grades: { id: number; description: string }[];
  subjects: { id: number; description: string }[];
  designations: { id: number; description: string }[];
  feesTypes: { id: number; description: string }[];
  teachers: { id: number; description: string }[];
  staff: { id: number; description: string }[];
};

const toOptions = (rows?: { id: number; description: string }[]): FilterOption[] =>
  (rows ?? []).map((row) => ({ value: row.id, label: row.description }));

export function useLookups() {
  const { data, loading } = useResource<Lookups>('/api/lookups');

  return {
    loading,
    grades: toOptions(data?.grades),
    subjects: toOptions(data?.subjects),
    designations: toOptions(data?.designations),
    feesTypes: toOptions(data?.feesTypes),
    teachers: toOptions(data?.teachers),
    staff: toOptions(data?.staff),
    active: ACTIVE_OPTIONS,
  };
}

/** How far the year filters reach either side of today. */
const YEARS_AHEAD = 2;
const YEARS_BACK = 10;

/**
 * Years to offer on the salary and fee period filters.
 *
 * Forward as well as back: Generate Advance bills up to three months ahead, so from October
 * onwards there are real fee rows in next year, and a filter that stopped at the current year
 * could not reach them. Newest first, so the recent years are at the top of the list.
 */
export const YEAR_OPTIONS: FilterOption[] = Array.from(
  { length: YEARS_AHEAD + YEARS_BACK + 1 },
  (_, index) => {
    const year = new Date().getFullYear() + YEARS_AHEAD - index;
    return { value: year, label: String(year) };
  },
);
